import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './components/AuthProvider';
import { ProvisionerForm } from './components/ProvisionerForm';
import { QueryForm } from './components/QueryForm';
import { TabContainer } from './components/TabContainer';
import { QRPreview } from './components/QRPreview';
import { ImageCompositor, PORTRAIT_CONFIG, LANDSCAPE_CONFIG } from './services/imageCompositor';
import { QRGenerator } from './services/qrGenerator';
import { ApiService, EnvironmentConfig } from './services/api';
import { environments, AWS_REGION } from './config';
import { configureAmplify } from './config/amplify';
import { googleDriveService } from './services/googleDrive';
import { GoogleDriveDialog } from './components/GoogleDriveDialog';
import './App.css';

const AuthenticatedApp: React.FC = () => {
  const { user, loading, signIn, signOut } = useAuth();
  const [environment, setEnvironment] = useState<string>('development');
  const [apiService, setApiService] = useState<ApiService>(
    new ApiService(environments.development.apiEndpoint)
  );
  const [availableEnvironments, setAvailableEnvironments] = useState<EnvironmentConfig[]>([]);
  const [loadingEnvironments, setLoadingEnvironments] = useState(false);
  const [environmentError, setEnvironmentError] = useState<string | null>(null);
  
  // QR Preview state
  const [activeTab, setActiveTab] = useState<string>('create');
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const [previewSerialNumber, setPreviewSerialNumber] = useState<string>('');
  const [previewOrientation, setPreviewOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [previewCanvas, setPreviewCanvas] = useState<HTMLCanvasElement | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewQrUrl, setPreviewQrUrl] = useState<string>(''); // Store the QR URL for TIFF generation
  const [tiffGenerating, setTiffGenerating] = useState<boolean>(false);
  
  // Google Drive state
  const [lastTiffUrl, setLastTiffUrl] = useState<string>('');
  const [lastTiffS3Key, setLastTiffS3Key] = useState<string>('');
  const [googleDriveUploading, setGoogleDriveUploading] = useState<boolean>(false);
  const [googleDriveDialogOpen, setGoogleDriveDialogOpen] = useState<boolean>(false);
  const [googleDriveDialogType, setGoogleDriveDialogType] = useState<'confirmation' | 'success' | 'error'>('confirmation');
  const [googleDriveUrl, setGoogleDriveUrl] = useState<string>('');
  const [googleDriveError, setGoogleDriveError] = useState<string>('');
  const [googleDriveUploadCompleted, setGoogleDriveUploadCompleted] = useState<boolean>(false);

  // Load available environments when user is authenticated
  useEffect(() => {
    if (user && !loadingEnvironments && availableEnvironments.length === 0) {
      setLoadingEnvironments(true);
      apiService.getAvailableEnvironments()
        .then(response => {
          setAvailableEnvironments(response.availableEnvironments);
          setEnvironmentError(null);
          // Set first available environment as default if current isn't available
          if (response.availableEnvironments.length > 0 && 
              !response.availableEnvironments.some(env => env.name === environment)) {
            setEnvironment(response.availableEnvironments[0].name);
          }
        })
        .catch(error => {
          console.error('Error loading environments:', error);
          if (error.response?.status === 403) {
            setEnvironmentError('No environment access granted. Please contact an administrator to assign you to a user group.');
          } else {
            setEnvironmentError('Failed to load available environments. Please try again.');
          }
          setAvailableEnvironments([]);
        })
        .finally(() => {
          setLoadingEnvironments(false);
        });
    }
  }, [user, apiService, loadingEnvironments, availableEnvironments.length, environment]);

  useEffect(() => {
    const config = environments[environment];
    
    // Reconfigure Amplify for the selected environment
    configureAmplify({
      region: AWS_REGION,
      userPoolId: config.cognitoUserPoolId,
      userPoolWebClientId: config.cognitoClientId,
      oauth: {
        domain: config.cognitoDomain,
        scope: ['openid', 'profile', 'email'],
        redirectSignIn: window.location.origin,
        redirectSignOut: window.location.origin,
        responseType: 'code',
      },
      apiEndpoint: config.apiEndpoint,
    });

    setApiService(new ApiService(config.apiEndpoint));
  }, [environment]);

  const handleEnvironmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEnvironment(e.target.value);
  };

  const handlePreviewUpdate = (imageUrl: string, serialNumber: string, orientation: 'portrait' | 'landscape', canvas: HTMLCanvasElement | null, qrUrl?: string) => {
    setPreviewImageUrl(imageUrl);
    setPreviewSerialNumber(serialNumber);
    setPreviewOrientation(orientation);
    setPreviewCanvas(canvas);
    if (qrUrl) setPreviewQrUrl(qrUrl);

    // Reset Google Drive upload state when new QR is generated
    setLastTiffUrl('');
    setGoogleDriveDialogOpen(false);
    setGoogleDriveError('');
    setGoogleDriveUrl('');
    setGoogleDriveUploadCompleted(false);
  };

  const handlePreviewLoadingChange = (isLoading: boolean) => {
    setPreviewLoading(isLoading);
  };

  const handleDownloadPng = async () => {
    if (previewCanvas && previewSerialNumber && previewOrientation) {
      try {
        const orientationCapitalized = previewOrientation.charAt(0).toUpperCase() + previewOrientation.slice(1) as 'Portrait' | 'Landscape';
        // Download current PNG preview
        await ImageCompositor.downloadCompositePng(
          previewCanvas,
          previewSerialNumber,
          orientationCapitalized
        );
      } catch (err: any) {
        console.error('Failed to download PNG:', err);
      }
    }
  };


  const handleUploadToGoogleDrive = async () => {
    if (!previewSerialNumber || !previewOrientation) {
      return;
    }

    // Start generating TIFF immediately
    setTiffGenerating(true);

    try {
      // Generate TIFF first
      let qrUrl = previewQrUrl;
      if (!qrUrl) {
        const mapping = await apiService.getMapping(previewSerialNumber, environment);
        if (!mapping) {
          setTiffGenerating(false);
          setGoogleDriveError('Device mapping not found. Please create a mapping first.');
          setGoogleDriveDialogType('error');
          setGoogleDriveDialogOpen(true);
          return;
        }
        qrUrl = mapping.qrIdentifier;
      }

      // Generate QR code with same styling as frontend preview
      const config = previewOrientation === 'portrait' ? PORTRAIT_CONFIG : LANDSCAPE_CONFIG;
      const qrCodeCanvas = await QRGenerator.generateQRCodeWithSize(
        qrUrl,
        config.qrCodeWidth,
        config.qrCodeHeight,
        previewSerialNumber
      );

      // Convert QR code to base64
      const qrCodeDataUrl = qrCodeCanvas.toDataURL('image/png');

      // Generate TIFF composite on backend with QR code data
      const tiffData = await apiService.generateTiffComposite(
        previewSerialNumber,
        qrUrl,
        previewOrientation,
        environment,
        qrCodeDataUrl
      );

      // Store the TIFF URL for upload
      setLastTiffUrl(tiffData.downloadUrl);
      setLastTiffS3Key(tiffData.s3Key);
      setTiffGenerating(false);

      // Now show confirmation dialog
      setGoogleDriveDialogType('confirmation');
      setGoogleDriveDialogOpen(true);
    } catch (err: any) {
      console.error('Failed to generate TIFF:', err);
      setTiffGenerating(false);
      setGoogleDriveError('Failed to generate TIFF file. Please try again.');
      setGoogleDriveDialogType('error');
      setGoogleDriveDialogOpen(true);
    }
  };

  const handleGoogleDriveConfirm = async () => {
    setGoogleDriveDialogOpen(false);
    setGoogleDriveUploading(true);

    try {
      let tiffUrl = lastTiffUrl;

      // TIFF should already be generated from handleUploadToGoogleDrive
      if (!tiffUrl) {
        throw new Error('TIFF file not found. Please try again.');
      }

      // Download TIFF blob from S3 URL
      console.log('Downloading TIFF from S3 for Google Drive upload...');
      let response = await fetch(tiffUrl);
      let tiffBlob: Blob;

      if (!response.ok) {
        if (response.status === 403 && lastTiffS3Key) {
          // Try to refresh the URL
          console.log('TIFF URL expired, refreshing...');
          try {
            const refreshedData = await apiService.refreshTiffDownload(lastTiffS3Key, environment);
            tiffUrl = refreshedData.downloadUrl;
            setLastTiffUrl(tiffUrl);
            console.log('Retrying TIFF download with refreshed URL...');
            
            // Retry the download
            response = await fetch(tiffUrl);
            if (!response.ok) {
              throw new Error('TIFF file no longer available. Please regenerate preview.');
            }
            tiffBlob = await response.blob();
          } catch (refreshError) {
            console.error('Failed to refresh TIFF URL:', refreshError);
            throw new Error('TIFF file no longer available. Please regenerate preview.');
          }
        } else {
          throw new Error(`Failed to download TIFF: ${response.statusText}`);
        }
      } else {
        tiffBlob = await response.blob();
      }
      
      // Upload to Google Drive
      console.log('Uploading TIFF to Google Drive...');
      const result = await googleDriveService.uploadTiff(
        tiffBlob,
        previewSerialNumber,
        previewOrientation
      );

      // Automatically create the device mapping after successful upload
      console.log('Creating device mapping...');
      try {
        // Get the QR URL for the mapping
        let qrUrl = previewQrUrl;
        if (!qrUrl) {
          // Try to extract from the existing mapping if it exists
          const existingMapping = await apiService.getMapping(previewSerialNumber, environment);
          if (existingMapping) {
            qrUrl = existingMapping.qrIdentifier;
          } else {
            throw new Error('QR URL not found. Cannot create mapping.');
          }
        }

        // Create the mapping in the database
        await apiService.createMapping(
          previewSerialNumber,
          qrUrl,
          previewOrientation,
          true,  // imageCreated = true (we just created it)
          false, // etchCompleted = false (not yet etched)
          environment
        );
        console.log('Device mapping created successfully');
      } catch (mappingError) {
        // Log the error but don't fail the whole operation
        console.error('Failed to create device mapping:', mappingError);
        // You might want to show a warning to the user here
      }

      // Show success dialog
      setGoogleDriveUrl(result.webViewLink);
      setGoogleDriveDialogType('success');
      setGoogleDriveDialogOpen(true);
      setGoogleDriveUploadCompleted(true);

      console.log('Successfully uploaded to Google Drive:', result);
      
    } catch (error) {
      console.error('Google Drive upload failed:', error);
      setGoogleDriveError(error instanceof Error ? error.message : 'Unknown error occurred');
      setGoogleDriveDialogType('error');
      setGoogleDriveDialogOpen(true);
    } finally {
      setGoogleDriveUploading(false);
    }
  };

  const handleGoogleDriveDialogClose = () => {
    setGoogleDriveDialogOpen(false);
    setGoogleDriveError('');
    setGoogleDriveUrl('');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <h2>Loading...</h2>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-container">
        <h1>QR Code Provisioner</h1>
        <p>Please sign in with your Google account to continue</p>
        <button onClick={signIn} className="google-signin-btn">
          Sign in with Google
        </button>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-brand">
          <img src="/butterflymx-logo-black.png" alt="ButterflyMX" className="butterfly-logo" />
          <div>
            <h1>QR Code Provisioner</h1>
            <div className="brand-subtitle">Access Control Solutions</div>
          </div>
        </div>
        <div className="header-controls">
          <select value={environment} onChange={handleEnvironmentChange} disabled={loadingEnvironments || environmentError !== null}>
            {loadingEnvironments ? (
              <option>Loading environments...</option>
            ) : environmentError ? (
              <option>Access Denied</option>
            ) : availableEnvironments.length > 0 ? (
              availableEnvironments.map(env => (
                <option key={env.name} value={env.name}>
                  {env.displayName}
                </option>
              ))
            ) : (
              <option>No environments available</option>
            )}
          </select>
          <button onClick={signOut} className="logout-btn">Logout</button>
        </div>
      </header>
      <main className="app-main">
        {environmentError ? (
          <div className="error-message-container">
            <div className="error-message">
              <h2>⚠️ Access Restricted</h2>
              <p>{environmentError}</p>
            </div>
          </div>
        ) : (
          <div className="app-content">
            {/* Desktop: Left side - QR Preview (45%) - only show when there's preview content */}
            {/* Mobile: This will be moved below via CSS */}
            <div className={`qr-preview-section ${activeTab === 'create' && (previewImageUrl || previewLoading) ? 'has-content' : 'no-content'}`}>
              {activeTab === 'create' && (previewImageUrl || previewLoading) && (
                <QRPreview
                  previewImageUrl={previewImageUrl}
                  serialNumber={previewSerialNumber}
                  orientation={previewOrientation}
                  isLoading={previewLoading}
                  isTiffGenerating={tiffGenerating}
                  isGoogleDriveUploading={googleDriveUploading}
                  googleDriveUploadCompleted={googleDriveUploadCompleted}
                  onDownloadPng={handleDownloadPng}
                  onUploadToGoogleDrive={handleUploadToGoogleDrive}
                />
              )}
            </div>

            {/* Desktop: Right side - Tabbed interface */}
            {/* Mobile: This will be above the preview via CSS */}
            <div className={`form-section ${activeTab === 'create' && (previewImageUrl || previewLoading) ? 'has-preview' : 'no-preview'}`}>
              <div className="form-content">
                <TabContainer
                  defaultTab="create"
                  onTabChange={setActiveTab}
                  tabs={[
                  {
                    id: 'create',
                    label: 'Create Mapping',
                    icon: '🔗',
                    content: <ProvisionerForm
                      apiService={apiService}
                      environment={environment}
                      onPreviewUpdate={handlePreviewUpdate}
                      onLoadingChange={handlePreviewLoadingChange}
                      hasPreview={!!previewImageUrl}
                    />
                  },
                  {
                    id: 'query',
                    label: 'Query Mapping',
                    icon: '🔍',
                    content: <QueryForm apiService={apiService} environment={environment} />
                  }
                ]}
              />
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Google Drive Dialog */}
      <GoogleDriveDialog
        isOpen={googleDriveDialogOpen}
        type={googleDriveDialogType}
        serialNumber={previewSerialNumber}
        orientation={previewOrientation}
        googleDriveUrl={googleDriveUrl}
        errorMessage={googleDriveError}
        onConfirm={handleGoogleDriveConfirm}
        onCancel={handleGoogleDriveDialogClose}
      />
    </div>
  );
};

const App: React.FC = () => {
  const [configured, setConfigured] = useState(false);

  useEffect(() => {
    // Initialize with development config by default
    const config = environments.development;
    
    if (!config.cognitoUserPoolId || !config.cognitoClientId) {
      console.error('Cognito configuration missing');
      return;
    }

    configureAmplify({
      region: AWS_REGION,
      userPoolId: config.cognitoUserPoolId,
      userPoolWebClientId: config.cognitoClientId,
      oauth: {
        domain: config.cognitoDomain,
        scope: ['openid', 'profile', 'email'],
        redirectSignIn: window.location.origin,
        redirectSignOut: window.location.origin,
        responseType: 'code',
      },
      apiEndpoint: config.apiEndpoint,
    });
    
    setConfigured(true);
  }, []);

  if (!configured) {
    return (
      <div className="loading-container">
        <h2>Initializing...</h2>
      </div>
    );
  }

  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
};

export default App;